import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EmbeddedViewRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormattingService } from '../services/formatting.service';
import { CombinedDateMetadataService } from './combined-date.metadata.service';
import { DateUtil, DEFAULT_DATE_FORMAT } from '../services/date.util';
import moment, { Moment } from 'moment';
import { IPikadayOnDrawEvent, IPikadayOptions } from '@type_definitions/vendor';
import { LanguageService } from '../language/language.service';
import { autoCompleteDate } from './combined-date.util';
import { IFieldOption } from '../formly/ga-formly.service';
import Pikaday from 'pikaday';
import _ from 'underscore';

type ICombinedDateValue = string | Date;

interface ICombinedDateModel {
  symbolicValue?:ICombinedDateValue,
  dateValue?:ICombinedDateValue;
}

@Component({
  selector: 'combined-date',
  template: `
    <div class="combined-date">
      <div class="form-row align-items-center">
        <div class="col">
          <div class="input-group">
            <input class="form-control date-control" [ngClass]="classNames" type="text" [(ngModel)]="model.dateValue" (ngModelChange)="onDateValueChange()" [hidden]="showSymbolic" autocompletetype="">
            <select class="form-select form-select-sm date-control" [(ngModel)]="model.symbolicValue" [ngClass]="classNames" (change)="onSymbolicValueChange()" *ngIf="showSymbolic">
              <option *ngFor="let option of symbolicOptions" [ngValue]="option.value">{{ option.name }}</option>
            </select>
            <span class="input-group-btn"><button class="btn btn-default" type="button" tabIndex="-1"><i class="fa fa-calendar"></i></button></span>
          </div>
        </div>
      </div>
    </div>

    <ng-template #pikaExtraTpl>
      <span class="pika-title pika-extra-template">
        <div class="pika-label">
          {{ symbolicDateLabel() }}
          <select class="pika-select pika-select-symbolic" [(ngModel)]="model.symbolicValue" [ngClass]="classNames" (change)="onSymbolicValueChange()">
            <option *ngFor="let option of symbolicOptions" [ngValue]="option.value">{{ option.name }}</option>
          </select>
        </div>
      </span>
    </ng-template>
  `,
  styleUrls: ['./combined-date.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CombinedDateComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() public value:ICombinedDateValue;
  @Input() public classNames:string;
  @Output() public valueChange:EventEmitter<ICombinedDateValue> = new EventEmitter<ICombinedDateValue>();

  public model:ICombinedDateModel = {
    dateValue: null,
    symbolicValue: null
  };
  public showSymbolic:boolean;
  public symbolicOptions:IFieldOption[];

  @ViewChild('pikaExtraTpl') private pikaExtraTpl:TemplateRef<HTMLElement>;
  private picker:Pikaday;
  private _dateValue:string;

  constructor(
    private elementRef:ElementRef<HTMLElement>,
    private formattingService:FormattingService,
    private combinedDateMetadataService:CombinedDateMetadataService,
    private languageService:LanguageService,
    private dateUtil:DateUtil,
    private viewContainerRef:ViewContainerRef,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    /**/
  }

  public ngOnInit():void {
    Object.defineProperty(this.model, 'dateValue', {
      get: () => this._dateValue,
      set: (value):void => {
       this._dateValue = value;
       this.onDateValueChange();
      }
    });
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.value) {
      let value:string;
      if (this.dateUtil.testSysdate(changes.value.currentValue)) {
        value = changes.value.currentValue;
      } else {
        value = this.formattingService.formatDate(changes.value.currentValue);
      }
      // we only update value coming from input binding when it's different from current model
      if (!_.isEqual(value, this.dateValue)) {
        // add timeout to let picker be fully initialized
        setTimeout(() => {
          this.parseValue();
        });
      }
    }
  }

  public ngAfterViewInit():void {
    this.initPikaday();
    this.initInputMask();
    this.populateOptions();
    this.parseValue();
  }

  public symbolicDateLabel():string {
    const defaultLabel:string = 'Choose Date';
    if (!this.model.symbolicValue) {
      return defaultLabel;
    }
    const option:IFieldOption = _.findWhere(this.symbolicOptions, { value: this.model.symbolicValue });
    return option?.name || defaultLabel;
  }

  public onSymbolicValueChange():void {
    this.showSymbolic = true;
    this.picker.hide();
    this.syncSymbolicDate();

    if (this.showSymbolic) {
      this.value = this.model.symbolicValue? this.model.symbolicValue : null;
      this.notifyChange();
    }

    this.changeDetectorRef.detectChanges();
  }

  public onDateValueChange():void {
    if (!this.showSymbolic) {
      this.value = this.model.dateValue? this.formattingService.parseDate(<string>this.model.dateValue) : null;
      this.notifyChange();
    }

    this.changeDetectorRef.detectChanges();
  }

  private initPikaday():void {
    const _this:CombinedDateComponent = this;
    const pikadayOptions:IPikadayOptions = {
      field: this.elementRef.nativeElement.querySelector('.date-control'),
      format: DEFAULT_DATE_FORMAT,
      // cannot be a fat-arrow function because of this
      onSelect: function() {
        const m:Moment = this.getMoment();
        if (m) {
          //make this a UTC date
          const d:Date = moment.utc([
            m.year(),
            m.month(),
            m.date()
          ]).toDate();

          _this.showSymbolic = false;
          _this.model.dateValue = _this.formattingService.formatDate(d);
          _this.model.symbolicValue = undefined;
        }
      },
      onDraw: function(picker:IPikadayOnDrawEvent):void {
        const embeddedViewRef:EmbeddedViewRef<HTMLElement> = _this.viewContainerRef.createEmbeddedView(_this.pikaExtraTpl);

        for (const node of embeddedViewRef.rootNodes) {
         picker.el.append(node);
        }
      },
      trigger: this.elementRef.nativeElement.querySelector('.btn'),
      i18n: this.languageService.translateDatepicker()
    };

    this.picker = new Pikaday(<any>pikadayOptions);
  }

  private initInputMask() {
    const input:JQuery<HTMLElement> = $(this.elementRef.nativeElement).find('input');

    input.inputmask('dd/mm/yyyy');

    input.on('blur', ():void => {
      const dateValue:string = input.val();
      let parsedDate:Date = null;
      if (dateValue) {
        parsedDate = this.formattingService.parseDate(autoCompleteDate(input));
      }
      this.picker.setDate(parsedDate, true);
      this.model.dateValue = parsedDate;
      setTimeout(() => {
        input.val(dateValue);
      });
    });
  }

  private populateOptions():void {
    // add empty option
    this.symbolicOptions = _.union(this.combinedDateMetadataService.getMetaData(), [
      {
        name: '',
        value: null
      }
    ]);
  }

  private parseValue():void {
    if (this.value) {
      this.showSymbolic = this.dateUtil.testSysdate(<string>this.value);
    }

    if (this.showSymbolic) {
      this.model.symbolicValue = <string>this.value;
      this.syncSymbolicDate();
      this.validateOptions();
    } else {
      this.model.dateValue = this.formattingService.formatDate(this.value);
    }

    this.changeDetectorRef.detectChanges();
  }

  private get dateValue():ICombinedDateValue {
    if (this.showSymbolic) {
      return this.model.symbolicValue;
    } else {
      return this.model.dateValue;
    }
  }

  private syncSymbolicDate() {
    // sync the picker date value with the sysdate value
    const parsedDate:Date = this.model.symbolicValue && this.dateUtil.parseSysdate(<string>this.model.symbolicValue);
    this.picker.setDate(parsedDate, true);
  }

  private validateOptions():void {
    const option:IFieldOption = _.findWhere(this.symbolicOptions, { value: this.value });
    if (!option) {
      this.symbolicOptions.push({
        name: <string>this.value,
        value: <string>this.value
      });
    }
  }

  private notifyChange():void {
    this.valueChange.emit(this.value);
  }

  public ngOnDestroy() {
    this.picker?.destroy();
  }
}